import React, { useState } from "react";
import { useAppConfig } from "../../context/AppConfigContext";
import styles from "./Referral.module.css";

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  try {
    return document.execCommand("copy");
  } finally {
    document.body.removeChild(textarea);
  }
};

const ShareSection = ({ platform, referralLink, setMessage, isNative }) => {
  const { t, brandName } = useAppConfig();
  const [isSharing, setIsSharing] = useState(false);

  const shareTitle = brandName;
  const shareText =
    t.referrals?.share_text ||
    `I learn languages with ${brandName}. Premium is included for 7 days via my link.`;
  //const sharePayloadText = referralLink ? `${shareText}\n${referralLink}` : shareText;
  const sharePayloadText = shareText;

  const handleCopy = async () => {
    if (!referralLink) {
      setMessage(t.referrals?.error || "Error loading referrals");
      return;
    }

    try {
      const copied = await copyToClipboard(referralLink);
      setMessage(
        copied
          ? t.referrals?.copied || "Copied!"
          : t.referrals?.copy_error || "Copy is not available on this device",
      );
    } catch (_) {
      setMessage(t.referrals?.copy_error || "Copy is not available on this device");
    }
  };

  const getNativeShareApi = async () => {
    try {
      const module = await import("@capacitor/share");
      if (module?.Share?.share) {
        return module.Share;
      }
    } catch (_) {
      // Fallback below.
    }

    const legacyShare = window?.Capacitor?.Plugins?.Share;
    if (legacyShare?.share) {
      return legacyShare;
    }

    return null;
  };

  const handleShare = async () => {
    if (!referralLink) {
      setMessage(t.referrals?.error || "Error loading referrals");
      return;
    }

    if (isSharing) return;
    setIsSharing(true);

    try {
      if (platform === "telegram" && window.Telegram?.WebApp?.shareLink) {
        window.Telegram.WebApp.shareLink(referralLink, shareText);
        return;
      }

      if (isNative) {
        const nativeShare = await getNativeShareApi();
        if (nativeShare?.share) {
          try {
            await nativeShare.share({
              title: shareTitle,
              text: sharePayloadText,
              url: referralLink,
            });
            return;
          } catch (_) {
            await handleCopy();
            return;
          }
        } else {
          await handleCopy();
          return;
        }
      }

      if (navigator.share) {
        try {
          await navigator.share({
            title: shareTitle,
            text: sharePayloadText,
            url: referralLink,
          });
          return;
        } catch (error) {
          if (error?.name === "AbortError") {
            return;
          }
        }
      }

      await handleCopy();
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className={styles.shareBlock}>
      <div className={styles.shareCard}>
        <button className={styles.shareButton} onClick={handleShare} disabled={isSharing}>
          {platform === "telegram"
            ? t.referrals?.share_telegram || "Share in Telegram"
            : t.referrals?.share_button || "Share invite link"}
        </button>

        <button className={styles.copyButton} onClick={handleCopy} disabled={isSharing}>
          {t.referrals?.copy_button || "Copy link"}
        </button>
      </div>
    </div>
  );
};

export default ShareSection;
